import { type ReactNode } from 'react';

import { ListPageHeader } from '@apps/web/components/ui/list_page_header';
import {
    formatDateKeyLong,
    formatDateKeyRange,
} from '@apps/web/lib/date_utils';

/**
 * The heading both shift presentations render.
 *
 * The calendar and the table are two views of one page, so they must agree on
 * what the page is called and on what "28 shifts · 168 hours" means for a
 * given range. Keeping the wording and the counting rule here is what stops
 * the two panes from drifting into two different pages again.
 */

// Structural on purpose: the table reads paged `/shifts/search` rows and the
// calendar reads the `/shifts/calendar` projection. Both carry these fields.
export type CountableShift = {
    readonly status: string;
    readonly latest_stage?: string | null;
    readonly assigned_caregiver_id?: string | null;
    readonly shift_start_time: string;
    readonly shift_end_time: string;
};

export function shouldCountShiftInHeading(shift: CountableShift): boolean {
    if (shift.status === 'cancelled' || shift.latest_stage === 'cancelled') {
        return false;
    }

    return Boolean(shift.assigned_caregiver_id) || shift.status === 'open';
}

export function getShiftDurationH(shift: CountableShift): number {
    return (
        (new Date(shift.shift_end_time).getTime() -
            new Date(shift.shift_start_time).getTime()) /
        3_600_000
    );
}

export function formatShiftsHeadingTitle(
    fromDateKey: string,
    toDateKey: string,
    timezone: string,
): string {
    const rangeLabel =
        fromDateKey === toDateKey
            ? formatDateKeyLong(fromDateKey, timezone)
            : formatDateKeyRange(fromDateKey, toDateKey, timezone);
    return `Shifts for ${rangeLabel}`;
}

export function formatShiftsHeadingSummary({
    shiftCount,
    shiftHours,
    isToday = false,
}: {
    readonly shiftCount: number;
    readonly shiftHours: number;
    readonly isToday?: boolean;
}): string {
    const shiftLabel = shiftCount === 1 ? 'shift' : 'shifts';
    const hourLabel = shiftHours === 1 ? 'hour' : 'hours';
    const formattedHours = new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 2,
    }).format(shiftHours);
    const summary = `${shiftCount} ${shiftLabel} · ${formattedHours} ${hourLabel}`;
    return isToday ? `${summary} today` : summary;
}

export function ShiftsPageHeading({
    title,
    summary,
    titleAdornment,
    footer,
    redesigned = false,
}: {
    readonly title: string;
    readonly summary: ReactNode;
    // Sits inline after the title (for example a tour replay button).
    readonly titleAdornment?: ReactNode;
    readonly footer?: ReactNode;
    readonly redesigned?: boolean;
}) {
    if (redesigned) {
        return (
            <div className="min-w-0">
                <ListPageHeader
                    title={title}
                    description={summary}
                    actions={titleAdornment}
                    className="mb-0"
                />
                {footer}
            </div>
        );
    }

    return (
        <div className="min-w-0">
            <div className="flex items-center gap-2">
                <h1 className="text-lg leading-6 font-semibold md:text-xl md:leading-normal">
                    {title}
                </h1>
                {titleAdornment}
            </div>
            <div className="text-muted-foreground mt-0.5 text-sm md:mt-1">
                {summary}
            </div>
            {footer}
        </div>
    );
}
